import React from 'react';
import { LocalizedMessage, LocalizedPrice, LocalizedStandardDateNoSeconds } from 'intl/LocalizedComponents';
import _isEmpty from 'lodash/isEmpty';
import _map from 'lodash/map';
import _size from 'lodash/size';
import _toString from 'lodash/toString';
import PropTypes from 'prop-types';
// Core components
import Divider from 'components/core/Divider';
import List from 'components/core/List';
// Permissions
import permissions from 'constants/Permissions';
import OnlyWith from 'components/permissions/OnlyWith';
// Models
import mFinance from 'models/cpm/finance';
import mParking from 'models/cpm/parking';
import mBooking from 'models/cpm/booking';
import mPriceRule from 'models/cpm/priceRule';
// Custom
import { cpmBookingDataShape } from 'constants/PropTypesShapes';
import { dateGenerator } from 'utils/DateUtils';
import InformationItem from 'components/items/InformationItem';
import {
  SpacesQuantityItem, StatusItem, DateBeginItem, DateEndItem,
} from 'components/items/InformationItems';
import Callout from 'components/misc/Callout';
import ParkingCategoryAltItem from 'cpmCarparks/assets/parkingCategories/ParkingCategoryAltItem';

const BookingTooltipContent = ({
  booking, timezone, withHeaderMessage, withParkingCategory, withStatus,
}) => {
  const {
    begin, end, spacesQuantity, keypadCodes, vehicles,
  } = booking;
  const purchase = mFinance.lastPurchase(booking);

  // Status
  const { labelId: statusLabelId, icon: statusIcon, color: statusColor } = mBooking.bookingStatus(booking);

  // Price
  const { labelId: priceTypeLabelId, icon: priceTypeIcon } = mFinance.lastPurchasePriceType(booking) || {};

  // Keypad codes
  const keypadCodesInfo = _size(keypadCodes) > 1 ? `(+${_size(keypadCodes) - 1})` : '';

  // License plates
  const licensePlates = _map(vehicles, 'licensePlate');
  const licensePlatesInfo = _size(licensePlates) > 1 ? `(+${_size(licensePlates) - 1})` : '';

  // For header message
  let headerMessage;
  if (withHeaderMessage) {
    const severity = 'success';
    headerMessage = {
      severity,
      content: <LocalizedMessage id="info.bookingStatusDesc" values={{ status: mBooking.bookingStatus(booking).value }} />,
    };
  }

  // Permissions
  const {
    pCpm: { parkingCategoryRead },
  } = permissions;

  return (
    <List disablePadding>
      {headerMessage && (
        <div className="py-1">
          <Callout withInfoIcon={false} severity={headerMessage.severity}>
            {headerMessage.content}
          </Callout>
        </div>
      )}
      {withParkingCategory && (
        <OnlyWith
          permissions={{
            needed: parkingCategoryRead,
            permissionsOrContext: 'carparkPermissions',
          }}
        >
          <React.Fragment>
            <ParkingCategoryAltItem light parkingCategoryId={mParking.getParkingCategoryId(booking)} />
            <Divider variant="middle" />
          </React.Fragment>
        </OnlyWith>
      )}
      <SpacesQuantityItem
        light
        value={<LocalizedMessage id="info.spacesQuantity" values={{ quantity: _toString(spacesQuantity) }} />}
      />
      {begin && (
        <React.Fragment>
          <Divider variant="middle" />
          <DateBeginItem
            light
            value={<LocalizedStandardDateNoSeconds value={dateGenerator.dateFromTimezone(begin, timezone)} />}
          />
        </React.Fragment>
      )}
      {end && (
        <React.Fragment>
          <Divider variant="middle" />
          <DateEndItem
            light
            value={<LocalizedStandardDateNoSeconds value={dateGenerator.dateFromTimezone(end, timezone)} />}
          />
        </React.Fragment>
      )}
      {purchase && (
        <React.Fragment>
          <Divider variant="middle" />
          <InformationItem
            light
            icon={priceTypeIcon}
            value={
              mFinance.lastPurchasePriceIsFree(booking) ? (
                <LocalizedMessage id={priceTypeLabelId} />
              ) : (
                <LocalizedPrice
                  currency={purchase.priceRule.currency}
                  value={mPriceRule.displayAmount(purchase.totalAmountInclTax, purchase.priceRule)}
                />
              )
            }
          />
        </React.Fragment>
      )}
      {!_isEmpty(keypadCodes) && (
        <React.Fragment>
          <Divider variant="middle" />
          <InformationItem light icon="cAwTypeKCode" value={`${keypadCodes[0]} ${keypadCodesInfo}`} />
        </React.Fragment>
      )}
      {!_isEmpty(licensePlates) && (
        <React.Fragment>
          <Divider variant="middle" />
          <InformationItem light icon="cAwTypeLPlate" value={`${licensePlates[0]} ${licensePlatesInfo}`} />
        </React.Fragment>
      )}
      {withStatus && (
        <React.Fragment>
          <Divider variant="middle" />
          <StatusItem light icon={statusIcon} iconColor={statusColor} value={<LocalizedMessage id={statusLabelId} />} />
        </React.Fragment>
      )}
    </List>
  );
};

BookingTooltipContent.propTypes = {
  booking: cpmBookingDataShape.isRequired,
  timezone: PropTypes.string.isRequired,
  withHeaderMessage: PropTypes.bool,
  withParkingCategory: PropTypes.bool,
  withStatus: PropTypes.bool,
};

BookingTooltipContent.defaultProps = {
  withHeaderMessage: false,
  withParkingCategory: false,
  withStatus: false,
};

module.exports = BookingTooltipContent;



// WEBPACK FOOTER //
// ./routes/cpm/routes/carparks/assets/bookings/BookingTooltipContent.js